import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Target, TrendingUp, MessageCircle, CalendarCheck, ChevronDown, ChevronUp,
  Sparkles, Plus, RefreshCw, ExternalLink, Star,
} from "lucide-react";

function formatUSD(n) {
  if (!n) return "$0";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function PageHeader({ overline, title, right }) {
  return (
    <div className="border-b border-navy/10 px-8 py-8 bg-cream">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="overline text-gold-deep">{overline}</div>
          <h1 className="font-serif text-3xl md:text-4xl text-navy mt-2">{title}</h1>
          <div className="gold-rule mt-4" />
        </div>
        {right}
      </div>
    </div>
  );
}

function ProgressBar({ pct, label, accent }) {
  return (
    <div>
      <div className="flex justify-between text-[10px] uppercase tracking-widest text-navy/55 mb-1">
        <span>{label}</span>
        <span className={accent ? "text-gold-deep font-medium" : ""}>{pct}%</span>
      </div>
      <div className="h-1.5 bg-navy/10 overflow-hidden">
        <div
          className={`h-full transition-all ${accent ? "bg-gold" : "bg-navy/40"}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
}

function StatPill({ label, value, accent, icon: Icon }) {
  return (
    <div className={`bg-white border ${accent ? "border-gold" : "border-navy/10"} p-5 flex-1 min-w-[140px]`}>
      <div className="overline text-navy/60 flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="font-serif text-3xl text-navy mt-1">{value}</div>
    </div>
  );
}

export default function Pipeline() {
  const [data, setData] = useState(null);
  const [includeClosed, setIncludeClosed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [insightCache, setInsightCache] = useState({}); // deal_id -> {next_3, ...}

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/pipeline/insights?include_closed=${includeClosed}`);
        if (!cancelled) setData(d);
      } catch (err) {
        if (!cancelled) toast.error(formatApiError(err.response?.data?.detail) || "Failed to load pipeline");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [includeClosed]);

  const toggle = async (dealId) => {
    const open = !expanded[dealId];
    setExpanded((e) => ({ ...e, [dealId]: open }));
    if (open && !insightCache[dealId]) {
      try {
        const { data: detail } = await api.get(`/deals/${dealId}/insights`);
        setInsightCache((c) => ({ ...c, [dealId]: detail }));
      } catch {
        toast.error("Couldn't load deal-level insights");
      }
    }
  };

  const linkAndQueue = async (dealId, investorId, investorName, alreadyLinked) => {
    try {
      if (!alreadyLinked) {
        await api.post(`/investors/${investorId}/link-deal/${dealId}`);
      }
      // Refresh this deal's insight
      const { data: detail } = await api.get(`/deals/${dealId}/insights`);
      setInsightCache((c) => ({ ...c, [dealId]: detail }));
      toast.success(`${investorName} ${alreadyLinked ? "ready to chase" : "linked + ready to chase"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Action failed");
    }
  };

  return (
    <div data-testid="pipeline-page">
      <PageHeader
        overline="Sales Operating System"
        title="Pipeline Insights"
        right={
          <div className="flex items-center gap-3">
            <label className="text-xs uppercase tracking-widest text-navy/60 flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeClosed}
                onChange={(e) => setIncludeClosed(e.target.checked)}
                data-testid="pipeline-include-closed"
                className="h-3.5 w-3.5 accent-gold"
              />
              Show closed deals
            </label>
          </div>
        }
      />

      <div className="p-8 space-y-6">
        {loading && <div className="text-navy/40 italic">Loading pipeline…</div>}

        {!loading && data && (
          <>
            {/* Aggregate totals */}
            <div className="flex flex-wrap gap-3" data-testid="pipeline-totals">
              <StatPill label="Active Deals" value={data.totals.deals_count} icon={Target} />
              <StatPill label="Investors Targeted" value={data.totals.targeted} />
              <StatPill
                label="Past First Touch"
                value={`${data.totals.past_first_touch_pct}%`}
                accent={data.totals.past_first_touch_pct >= 50}
                icon={TrendingUp}
              />
              <StatPill
                label="Response Rate"
                value={`${data.totals.responded_pct}%`}
                accent={data.totals.responded_pct >= 20}
                icon={MessageCircle}
              />
              <StatPill
                label="Meetings Booked"
                value={data.totals.meetings_booked}
                accent={data.totals.meetings_booked > 0}
                icon={CalendarCheck}
              />
            </div>

            {/* Per-deal cards */}
            {data.deals.length === 0 ? (
              <div className="bg-white border border-navy/10 p-12 text-center text-navy/40 italic">
                {includeClosed
                  ? "No deals in your pipeline yet."
                  : "No active deals. Toggle 'Show closed deals' to see historical performance."}
              </div>
            ) : (
              <div className="space-y-4">
                {data.deals.map((d) => (
                  <DealCard
                    key={d.deal_id}
                    deal={d}
                    expanded={!!expanded[d.deal_id]}
                    onToggle={() => toggle(d.deal_id)}
                    detail={insightCache[d.deal_id]}
                    onLinkAndQueue={linkAndQueue}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DealCard({ deal, expanded, onToggle, detail, onLinkAndQueue }) {
  const healthy = deal.responded_pct >= 20 || deal.meetings_booked > 0;
  const struggling = deal.targeted > 0 && deal.past_first_touch_pct < 25;

  return (
    <div
      className="bg-white border border-navy/10 overflow-hidden"
      data-testid={`pipeline-deal-${deal.deal_id}`}
    >
      <button
        onClick={onToggle}
        className="w-full text-left p-5 hover:bg-cream/40 transition-colors"
        data-testid={`pipeline-deal-toggle-${deal.deal_id}`}
      >
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-serif text-xl text-navy">{deal.deal_name}</span>
              <span className="text-[10px] uppercase tracking-widest bg-navy/10 text-navy/70 px-2 py-0.5">
                {deal.stage}
              </span>
              {healthy && (
                <span className="text-[10px] uppercase tracking-widest bg-gold text-navy px-2 py-0.5">
                  Healthy
                </span>
              )}
              {struggling && (
                <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-2 py-0.5">
                  Stalled
                </span>
              )}
              {deal.targeted === 0 && (
                <span className="text-[10px] uppercase tracking-widest bg-navy/5 text-navy/50 px-2 py-0.5">
                  No targets yet
                </span>
              )}
            </div>
            <div className="text-xs text-navy/55 mt-1">
              {[deal.sector, deal.country].filter(Boolean).join(" · ")} · Ticket {formatUSD(deal.ticket_size_usd)}
            </div>
          </div>
          {expanded ? <ChevronUp className="h-5 w-5 text-navy/40" /> : <ChevronDown className="h-5 w-5 text-navy/40" />}
        </div>

        {/* Four-up metric grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-5">
          <div>
            <div className="overline text-navy/55">Targeted</div>
            <div className="font-serif text-2xl text-navy mt-1">{deal.targeted}</div>
          </div>
          <div>
            <ProgressBar pct={deal.past_first_touch_pct} label="Past First Touch" />
          </div>
          <div>
            <ProgressBar pct={deal.responded_pct} label="Responded" accent />
          </div>
          <div>
            <div className="overline text-navy/55">Meetings</div>
            <div className={`font-serif text-2xl mt-1 ${deal.meetings_booked > 0 ? "text-gold-deep" : "text-navy"}`}>
              {deal.meetings_booked}
            </div>
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-navy/10 bg-cream/40 p-5 space-y-5" data-testid={`pipeline-deal-detail-${deal.deal_id}`}>
          {/* Outreach status breakdown */}
          <div>
            <div className="overline text-gold-deep mb-2">Outreach status breakdown</div>
            <div className="flex flex-wrap gap-2">
              {Object.entries(deal.by_status || {}).map(([status, count]) => (
                <span
                  key={status}
                  className="text-xs px-3 py-1.5 bg-white border border-navy/10"
                >
                  <span className="text-navy/55 uppercase tracking-widest text-[10px]">{status}</span>
                  <span className="ml-2 font-medium text-navy">{count}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Next 3 to chase */}
          <div>
            <div className="overline text-gold-deep mb-2 flex items-center gap-2">
              <Sparkles className="h-3 w-3" /> Next 3 to chase
            </div>
            {!detail ? (
              <div className="text-sm italic text-navy/40">Loading recommendations…</div>
            ) : detail.next_3.length === 0 ? (
              <div className="text-sm italic text-navy/40">
                Every high-fit investor has already been touched for this deal. Nice work.
              </div>
            ) : (
              <div className="space-y-2" data-testid={`pipeline-next3-${deal.deal_id}`}>
                {detail.next_3.map((inv, idx) => (
                  <div
                    key={inv.id}
                    className="bg-white border border-navy/10 p-3 flex items-start justify-between gap-3"
                    data-testid={`next3-${inv.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-navy">#{idx + 1} {inv.name}</span>
                        <span className="text-[10px] uppercase tracking-widest bg-gold/15 text-gold-deep px-2 py-0.5">
                          Score {inv.fit_match_score}
                        </span>
                        <div className="flex items-center gap-0.5 text-gold-deep">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`h-3 w-3 ${i < (inv.fit_score || 0) ? "fill-current" : ""}`} />
                          ))}
                        </div>
                        {inv.already_linked && (
                          <span className="text-[10px] uppercase tracking-widest text-navy/55">
                            (already linked · {inv.current_status})
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-navy/55 mt-1">
                        {inv.investor_type} · {inv.hq_city}{inv.hq_country ? `, ${inv.hq_country.split(",")[0]}` : ""}
                      </div>
                      <div className="text-xs text-navy/65 mt-1 truncate">
                        {inv.sector_fit || "—"}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 whitespace-nowrap">
                      <button
                        onClick={() => onLinkAndQueue(deal.deal_id, inv.id, inv.name, inv.already_linked)}
                        data-testid={`next3-link-${inv.id}`}
                        className="text-xs uppercase tracking-widest text-gold-deep hover:underline flex items-center gap-1"
                      >
                        {inv.already_linked ? (
                          <>
                            <RefreshCw className="h-3 w-3" /> Refresh
                          </>
                        ) : (
                          <>
                            <Plus className="h-3 w-3" /> Link to deal
                          </>
                        )}
                      </button>
                      <Link
                        to={`/dashboard/investors?deal=${deal.deal_id}`}
                        className="text-[10px] uppercase tracking-widest text-navy/45 hover:text-navy flex items-center gap-1"
                      >
                        Open <ExternalLink className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer actions */}
          <div className="pt-3 border-t border-navy/10 flex justify-end gap-3">
            <Link
              to={`/dashboard/investors?deal=${deal.deal_id}`}
              data-testid={`pipeline-view-investors-${deal.deal_id}`}
              className="text-xs uppercase tracking-widest border border-navy text-navy px-4 py-2 hover:bg-navy hover:text-gold"
            >
              View all targeted investors →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
